/* eslint-disable prettier/prettier */
import { FormInstance } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import _ from 'lodash'
import React, { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { startOfMonth, endOfMonth } from '../../../../../config/dayjsConfig'
import {
  useCreateReallocation,
  useCreateReallocationTarget,
  useDeleteReallocation,
  useDeleteReallocationTarget,
  useUpdateReallocation,
  useUpdateReallocationTarget
} from '../../../../../features/reallocation'

import {
  ReallocationFloatingBalance,
  ReallocationSource,
  ReallocationTarget,
  ReallocationTargetType,
  SourcePeriodType
} from '../../../../../types/reallocation/Reallocation'

const initialSourceValues = {
  actuals: false,
  budget: false,
  description: undefined,
  dimensionId: undefined,
  endDate: undefined,
  id: undefined,
  startDate: undefined,
  targets: undefined,
  keyfigureId: undefined,
  sourceId: undefined
}

const initialTargetValues = {
  accountFrom: undefined,
  accountTo: undefined,
  companyId: undefined,
  dimensionId: undefined,
  fixed: undefined,
  formulaId: undefined,
  type: ReallocationTargetType.range,
  account: undefined,
  id: undefined
  // type: ReallocationType.fixed
}

export type ReallocationContextType = {
  sourceForm: FormInstance<ReallocationSource>
  targetParent: ReallocationSource | null
  targetForm: FormInstance<ReallocationTarget>
  floatingValues: ReallocationFloatingBalance[]
  sourceModalVisible: boolean
  targetModalVisible: boolean
  handleNewSource: () => void
  handleEditSource: (reallocation: ReallocationSource) => void
  handleDeleteSource: (reallocation: ReallocationSource) => void
  handleSaveSource: () => void
  handleCloseSource: () => void
  handleNewTarget: (parent: ReallocationSource) => void
  handleCloseTarget: () => void
  handleSaveTarget: () => void
  handleEditTarget: (target: ReallocationTarget, parent: ReallocationSource) => void
  handleCopyTarget: (target: ReallocationTarget, parent: ReallocationSource) => void
  handleDeleteTarget: (target: ReallocationTarget, parent: ReallocationSource) => void
}

export const ReallocationContext = React.createContext<ReallocationContextType | null>(null)

interface ReallocationProviderProps {
  children?: ReactNode
}

const ReallocationProvider: React.FC<ReallocationProviderProps> = ({ children }) => {
  const { t } = useTranslation()

  const createSource = useCreateReallocation()
  const updateSource = useUpdateReallocation()
  const deleteSource = useDeleteReallocation()
  const createTarget = useCreateReallocationTarget()
  const updateTarget = useUpdateReallocationTarget()
  const deleteTarget = useDeleteReallocationTarget()

  const [sourceModalVisible, setSourceModalVisible] = useState(false)
  const [targetModalVisible, setTargetModalVisible] = useState(false)
  const [sourceForm] = useForm<ReallocationSource>()
  const [targetForm] = useForm<ReallocationTarget>()
  const [targetParent, setTargetParent] = useState<ReallocationSource | null>(null)
  const [floatingValues, setFloatingValues] = useState<ReallocationFloatingBalance[]>([])

  const transformToForm = (data: ReallocationSource) => ({
    ...data,
    periodType: data.startDate && data.endDate ? SourcePeriodType.fixedPeriod : SourcePeriodType.indefinitely,
    startDate: data.startDate, // DatePicker hoitaa YYYY-MM-DD -> Dayjs muunnoksen
    endDate: data.endDate // DatePicker hoitaa YYYY-MM-DD -> Dayjs muunnoksen
  })

  const transformToSave = ({ periodType, ...data }: ReallocationSource) => ({
    ...data,
    startDate:
      periodType === SourcePeriodType.indefinitely ? undefined : startOfMonth(data.startDate) ?? undefined,
    endDate: periodType === SourcePeriodType.indefinitely ? undefined : endOfMonth(data.endDate) ?? undefined
  })

  // Source funtions
  const handleNewSource = () => {
    sourceForm.setFieldsValue(initialSourceValues)
    setSourceModalVisible(true)
  }

  const handleEditSource = (data: ReallocationSource) => {
    sourceForm.setFieldsValue(transformToForm(_.cloneDeep(data)) || '')
    setSourceModalVisible(true)
  }
  const handleCloseSource = () => {
    sourceForm.resetFields()
    setSourceModalVisible(false)
  }

  const handleSaveSource = async () => {
    try {
      const values = await sourceForm.validateFields()
      if (!values.actuals && !values.budget) {
        sourceForm.setFields([{ name: 'actuals', errors: [t('reallocationsPage:dataType-error')] }])
        throw new Error('DataType error')
      }
      const transformedValues = transformToSave(values)

      if (values.id) {
        updateSource.mutate(transformedValues)
      }
      if (!values.id) {
        createSource.mutate(transformedValues)
      }

      sourceForm.resetFields()
      setSourceModalVisible(false)
    } catch (error) {
      console.log('error', error)
    }
  }

  const handleDeleteSource = (data: ReallocationSource) => {
    deleteSource.mutate(data)
  }

  // Target funtions
  const handleNewTarget = (parent: ReallocationSource) => {
    setTargetParent(parent)
    targetForm.setFieldsValue(initialTargetValues)
    setTargetModalVisible(true)
  }

  const handleEditTarget = (target: ReallocationTarget, parent: ReallocationSource) => {
    setTargetParent(parent)

    const fValues = parent?.targets?.find(ta => ta.id === target.id)?.floating
    fValues && setFloatingValues(fValues)

    targetForm.setFieldsValue(_.cloneDeep(target))
    setTargetModalVisible(true)
  }

  const handleCopyTarget = (target: ReallocationTarget, parent: ReallocationSource) => {
    setTargetParent(parent)

    const fValues = parent?.targets?.find(ta => ta.id === target.id)?.floating
    fValues && setFloatingValues(fValues)

    targetForm.setFieldsValue(_.cloneDeep({ ...target, id: undefined }))
    setTargetModalVisible(true)
  }

  const handleCloseTarget = () => {
    setTargetParent(null)

    targetForm.resetFields()
    setTargetModalVisible(false)
    setFloatingValues([])
  }

  const handleSaveTarget = async () => {
    if (!targetParent) return
    try {
      const values = await targetForm.validateFields()
      const oldfloatingValues = targetParent?.targets?.find(tar => tar.id === values.id)?.floating
      const newfloatingValues = _.uniqBy([...(values?.floating || []), ...(oldfloatingValues || [])], elem =>
        [elem.month, elem.year].join()
      ).filter(f => f.value !== 0)

      const newValues = {
        ...values,
        floating: newfloatingValues
      }

      if (values.id) {
        updateTarget.mutate({ target: newValues, source: targetParent })
      }
      if (!values.id) {
        createTarget.mutate({ target: newValues, source: targetParent })
      }

      sourceForm.resetFields()
      setTargetModalVisible(false)
      setFloatingValues([])
    } catch (error) {
      console.log('error', error)
    }
  }

  const handleDeleteTarget = (target: ReallocationTarget, parent: ReallocationSource) => {
    deleteTarget.mutate({ target, source: parent })
  }

  return (
    <ReallocationContext.Provider
      value={{
        sourceForm,
        targetParent,
        floatingValues,
        targetForm,
        sourceModalVisible,
        targetModalVisible,
        handleSaveSource,
        handleNewSource,
        handleEditSource,
        handleCloseSource,
        handleDeleteSource,
        handleNewTarget,
        handleSaveTarget,
        handleEditTarget,
        handleCopyTarget,
        handleCloseTarget,
        handleDeleteTarget
      }}
    >
      {children}
    </ReallocationContext.Provider>
  )
}

export default ReallocationProvider
